import React, { useEffect, useRef, useState } from "react";
import { REPEAT_CHOICES } from "../utils/habits.js";

/**
 * Inline "new habit" line at the bottom of a day.
 *
 * It opens as a question rather than a form: three written words — once,
 * weekly, daily — and nothing else. Picking one is what brings up the text
 * line, already focused, so you never have to think about the repeat *after*
 * naming the thing.
 *
 * Enter adds the habit and keeps the line open so several can be written in a
 * row. Escape leaves without saving; Enter on an empty line, or a tap anywhere
 * outside, closes it — banking whatever was already typed rather than throwing
 * it away.
 *
 * @param {(name: string, repeat: string) => void} onCreate
 * @param {() => void} onClose
 */
export default function HabitComposer({ onCreate, onClose }) {
  const [name, setName] = useState("");
  // null until the person picks one — that choice is what opens the text line.
  const [repeat, setRepeat] = useState(null);
  const inputRef = useRef(null);
  const rootRef = useRef(null);

  // Focus the moment the line appears, not on mount: on mount there is no
  // input yet, only the three choices.
  useEffect(() => {
    if (repeat) inputRef.current?.focus();
  }, [repeat]);

  /** Save what is in the box, if anything. Returns nothing — callers close. */
  const commit = () => {
    const trimmed = name.trim();
    if (trimmed && repeat) onCreate(trimmed, repeat);
    return Boolean(trimmed);
  };

  // Tapping anywhere else means "done here". pointerdown rather than click so
  // it also fires for the touch that starts a drag, and it is registered after
  // mount so the very tap that opened the composer cannot close it again.
  useEffect(() => {
    const onPointerDown = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      commit();
      onClose();
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
    // Deliberately no dependency list: re-subscribing each render is what keeps
    // the handler looking at the text as it stands right now.
  });

  const submit = (event) => {
    event.preventDefault();
    // Submitting an empty line is the natural way to say "I'm done".
    if (!commit()) {
      onClose();
      return;
    }
    setName("");
    inputRef.current?.focus();
  };

  return (
    <form ref={rootRef} onSubmit={submit} className="flex flex-col gap-3.5">
      {/* Written like a sentence, not like buttons — this is the first thing
          you see, and a day column is too narrow for three loud pills. */}
      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        {REPEAT_CHOICES.map((option) => {
          const active = repeat === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => setRepeat(option.value)}
              aria-pressed={active}
              className={`font-hand text-lg leading-none transition-colors ${
                active
                  ? "text-accent underline decoration-accent decoration-wavy underline-offset-4"
                  : "text-ink-soft hover:text-ink"
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {/* The name line only exists once a repeat has been chosen. */}
      {repeat && (
        <input
          ref={inputRef}
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => event.key === "Escape" && onClose()}
          placeholder="enter to submit"
          maxLength={60}
          aria-label="New habit"
          className="field-hand field-hand--accent min-w-0 font-hand text-lg leading-tight
                     placeholder:font-sans placeholder:text-xs placeholder:uppercase
                     placeholder:tracking-widest placeholder:text-ink-soft"
        />
      )}
    </form>
  );
}
